import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { interviewService } from '../../services/interviewService';
import { creditService } from '../../services/creditService';
import { resumeService } from '../../services/resumeService';
import { messageService } from '../../services/messageService';
import { analyticsService, DIM_LABELS, type PerformanceAnalytics } from '../../services/analyticsService';
import type { InterviewHistoryItem, LatestInterview } from '../../models/v2/interviews';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';
import OnboardingChecklist from '../../components/dashboard/OnboardingChecklist';
import RecentActivity from '../../components/features/dashboard/RecentActivity';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

import {
    RiAddLine,
    RiTimeLine,
    RiRobot2Line,
    RiArrowRightUpLine,
    RiCoinLine,
    RiCheckboxCircleFill,
    RiCheckboxBlankCircleLine,
    RiFileTextLine,
    RiMessage2Line,
    RiLineChartLine,
    RiArrowUpLine,
    RiArrowDownLine,
    RiFireLine,
    RiSparklingLine,
    RiVideoLine,
} from 'react-icons/ri';

// ── Analytics Preview Widget ─────────────────────────────────────────────────

function AnalyticsPreview({
    data,
    onViewAnalytics,
}: {
    data: PerformanceAnalytics | null;
    onViewAnalytics: () => void;
}) {
    if (!data || data.performance_over_time.length === 0) return null;

    const { summary, dimension_averages, performance_over_time } = data;
    const sparkData = performance_over_time.slice(-10).map((p, i) => ({ i, score: p.overall_score }));

    const weakestLabel = summary.weakest_dimension ? DIM_LABELS[summary.weakest_dimension as keyof typeof DIM_LABELS] : null;
    const bestLabel    = summary.best_dimension    ? DIM_LABELS[summary.best_dimension    as keyof typeof DIM_LABELS] : null;
    const improvement  = summary.improvement_pct;
    const improved     = improvement != null && improvement > 0;
    const declined     = improvement != null && improvement < 0;

    return (
        <GlassCard className="relative overflow-hidden">
            {/* Ambient glow */}
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative z-10">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                            <RiLineChartLine size={18} />
                        </div>
                        <div>
                            <h3 className="text-white font-semibold text-sm">Performance Snapshot</h3>
                            <p className="text-white/30 text-xs">{summary.total_sessions} sessions tracked</p>
                        </div>
                    </div>
                    <button
                        onClick={onViewAnalytics}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-white/50 hover:text-white hover:bg-white/[0.08] transition-all text-xs font-medium"
                    >
                        View Analytics <RiArrowRightUpLine size={13} />
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-center">

                    {/* Mini Sparkline */}
                    <div className="lg:col-span-1">
                        <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Score Trend</p>
                        <ResponsiveContainer width="100%" height={70}>
                            <AreaChart data={sparkData} margin={{ top: 2, right: 2, bottom: 0, left: -40 }}>
                                <defs>
                                    <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%"  stopColor="#a855f7" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="i" hide />
                                <YAxis domain={[0, 100]} hide />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        return (
                                            <div className="bg-[#0f0f14] border border-white/10 rounded-lg p-2 text-xs">
                                                <p className="text-white font-bold">{payload[0].value}</p>
                                            </div>
                                        );
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="score"
                                    stroke="#a855f7"
                                    strokeWidth={2}
                                    fill="url(#sparkGrad)"
                                    dot={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Key Stats */}
                    <div className="lg:col-span-1 grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                            <p className="text-white/30 text-[10px] uppercase tracking-wider">Avg Score</p>
                            <p className="text-white text-xl font-bold mt-0.5">{summary.avg_score ?? '–'}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                            <p className="text-white/30 text-[10px] uppercase tracking-wider">Improvement</p>
                            <p className={`text-xl font-bold mt-0.5 flex items-center gap-0.5 ${improved ? 'text-emerald-400' : declined ? 'text-red-400' : 'text-white/50'}`}>
                                {improved ? <RiArrowUpLine size={16} /> : declined ? <RiArrowDownLine size={16} /> : null}
                                {improvement != null ? `${Math.abs(improvement)}%` : '–'}
                            </p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                            <p className="text-white/30 text-[10px] uppercase tracking-wider">Streak</p>
                            <p className={`text-xl font-bold mt-0.5 flex items-center gap-1 ${(summary.current_streak ?? 0) >= 3 ? 'text-orange-400' : 'text-white'}`}>
                                {(summary.current_streak ?? 0) >= 3 && <RiFireLine size={16} />}
                                {summary.current_streak ?? 0}d
                            </p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                            <p className="text-white/30 text-[10px] uppercase tracking-wider">This Week</p>
                            <p className="text-white text-xl font-bold mt-0.5">{summary.sessions_this_week ?? 0}</p>
                        </div>
                    </div>

                    {/* AI Summary */}
                    <div className="lg:col-span-1 space-y-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                            <RiSparklingLine size={12} className="text-purple-400" />
                            <p className="text-white/30 text-[10px] uppercase tracking-wider font-semibold">AI Insight</p>
                        </div>
                        {bestLabel && (
                            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/[0.15]">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                                <p className="text-emerald-400/80 text-xs leading-relaxed">
                                    Strong <strong className="text-emerald-400">{bestLabel}</strong> ({dimension_averages[summary.best_dimension as keyof typeof dimension_averages]}/100) — keep it up.
                                </p>
                            </div>
                        )}
                        {weakestLabel && (
                            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/[0.06] border border-red-500/[0.15]">
                                <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                                <p className="text-red-400/80 text-xs leading-relaxed">
                                    Focus on <strong className="text-red-400">{weakestLabel}</strong> ({dimension_averages[summary.weakest_dimension as keyof typeof dimension_averages]}/100) — biggest gain opportunity.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </GlassCard>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Overview() {
    const navigate = useNavigate();
    const { user } = useAuth();

    // State - Initialize from Cache if available
    const cachedData = interviewService.getCachedHistory();
    const getSortedInterviews = (data: any[] | undefined) => {
        if (!data || data.length === 0) return [];
        return [...data].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    };

    const initialInterviewsRaw = cachedData
        ? (Array.isArray(cachedData) ? cachedData : cachedData.interviews || [])
        : [];
    const initialSorted = getSortedInterviews(initialInterviewsRaw);

    const [allInterviews, setAllInterviews] = useState<InterviewHistoryItem[]>(initialSorted);
    const [latestResult, setLatestResult] = useState<LatestInterview | null>(null);

    // Only show loading shimmer if we have no data
    const [loading, setLoading] = useState(!cachedData);
    const [resumeCount, setResumeCount] = useState(0);
    const [messageCount, setMessageCount] = useState(0);

    // Reuse Dialog State
    const [reuseConfirmOpen, setReuseConfirmOpen] = useState(false);
    const [selectedForReuse, setSelectedForReuse] = useState<InterviewHistoryItem | null>(null);
    const [credits, setCredits] = useState<number | null>(null);

    const handleReuseClick = (interview: any) => {
        setSelectedForReuse(interview);
        setReuseConfirmOpen(true);
    };

    const handleReuseConfirm = async () => {
        if (!selectedForReuse) return;
        try {
            // Call API
            const newSetup = await interviewService.reuseInterview(selectedForReuse.id);
            creditService.invalidate();
            setReuseConfirmOpen(false);
            navigate(`/video-interview/${newSetup.interview_id}`);
        } catch (err: any) {
            console.error("Reuse failed", err);
            // Handle errors (402, 404)
            if (err.status === 402 || err.response?.status === 402) {
                alert("Insufficient credits! Please top up to continue.");
            } else {
                alert("Failed to reuse interview. Please try again.");
            }
            setReuseConfirmOpen(false);
            setSelectedForReuse(null);
        }
    };

    useEffect(() => {
        const fetchInterviews = async () => {
            try {
                // Fetch History and Latest in parallel
                const [historyData, latestData] = await Promise.all([
                    interviewService.list(),
                    interviewService.getLatestInterview()
                ]);

                const interviews = Array.isArray(historyData) ? historyData : historyData.interviews || [];
                if (interviews.length > 0) {
                    const sorted = [...interviews].sort(
                        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                    setAllInterviews(sorted);
                }

                if (latestData.latest) {
                    setLatestResult(latestData.latest);
                }
            } catch (err) {
                console.error("Failed to fetch dashboard data", err);
            } finally {
                setLoading(false);
            }
        };
        fetchInterviews();
    }, []);

    useEffect(() => {
        creditService.getSummary().then(s => setCredits(s.available_credits)).catch(() => {});
        // Keep widget in sync whenever any action invalidates the credit cache
        const unsub = creditService.onUpdate(s => setCredits(s.available_credits));
        return unsub;
    }, []);

    useEffect(() => {
        resumeService.getGeneratedResumes().then(r => setResumeCount(r.length)).catch(() => {});
        messageService.list().then(m => setMessageCount(m.length)).catch(() => {});
    }, []);

    const [analyticsData, setAnalyticsData] = useState<PerformanceAnalytics | null>(null);

    useEffect(() => {
        analyticsService.getPerformance().then(setAnalyticsData).catch(() => {});
    }, []);

    const jobsDone = allInterviews.filter(i => i.status === 'completed').length;

    const JOBS_GOAL = 5;
    const MESSAGES_GOAL = 3;
    const RESUMES_GOAL = 5;

    const m1Done = jobsDone >= JOBS_GOAL;
    const m2Done = messageCount >= MESSAGES_GOAL;
    const m3Done = resumeCount >= RESUMES_GOAL;
    const getStartedComplete = m1Done && m2Done && m3Done;



    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">Dashboard Overview</h1>
                    <p className="text-gray-400">Welcome back, {user?.name || 'Candidate'}. Ready to prep?</p>
                </div>
                <GlassButton
                    onClick={() => navigate('/interview-mode')}
                    icon={<RiAddLine />}
                    className="shadow-brand-500/20"
                >
                    New Interview
                </GlassButton>
            </div>
            {/* OnboardingChecklist - Keep as is */}
            <OnboardingChecklist />

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left Column: Activity & Results (2/3) */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Recent Activity Card */}
                    <RecentActivity
                        interviews={allInterviews}
                        loading={loading}
                        onReuse={handleReuseClick}
                    />

                    {/* Latest Results Card Redesign */}
                    {latestResult && (
                        <div>
                            <GlassCard
                                className="relative overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:bg-white/[0.04] border-white/10 group h-full"
                                onClick={() => {
                                    if (latestResult.type === 'simulation') {
                                        navigate(`/simulation-result/${latestResult.id}`);
                                    } else {
                                        navigate(`/interview-detail/${latestResult.id}`);
                                    }
                                }}
                            >
                                {/* Gradient Orb Background */}
                                <div className="absolute -top-12 -right-12 w-48 h-48 bg-brand-500/10 rounded-full blur-[80px] pointer-events-none group-hover:bg-brand-500/20 transition-all duration-700" />

                                <div className="relative z-10 p-2">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                                                {latestResult.type === 'simulation' ? <RiRobot2Line size={20} /> : <RiVideoLine size={20} />}
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-white tracking-tight">Latest Refinement</h2>
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-[0.1em]">
                                                    {latestResult.type === 'simulation' ? 'AI Simulation' : 'Structured Interview'}
                                                </p>
                                            </div>
                                        </div>
                                        <RiArrowRightUpLine className="text-gray-600 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" size={20} />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                                        {/* Score Visual */}
                                        <div className="flex items-center gap-6">
                                            <div className="relative flex items-center justify-center">
                                                <svg className="w-28 h-28 transform -rotate-90">
                                                    <circle
                                                        cx="56" cy="56" r="50"
                                                        stroke="rgba(255,255,255,0.05)"
                                                        strokeWidth="8"
                                                        fill="transparent"
                                                    />
                                                    <circle
                                                        cx="56" cy="56" r="50"
                                                        stroke="white"
                                                        strokeWidth="8"
                                                        fill="transparent"
                                                        strokeDasharray={314.159}
                                                        strokeDashoffset={314.159 * (1 - latestResult.overall_score / 100)}
                                                        strokeLinecap="round"
                                                        className="transition-all duration-1000 ease-out"
                                                    />
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <span className="text-3xl font-black text-white">{Math.round(latestResult.overall_score)}%</span>
                                                </div>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white mb-1 truncate max-w-[200px]">{latestResult.job_title}</h3>
                                                <p className="text-sm text-gray-400 mb-2">{latestResult.company || 'Practice Session'}</p>
                                                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                                    <RiTimeLine size={12} />
                                                    {latestResult.created_at ? new Date(latestResult.created_at).toLocaleDateString() : 'Last Session'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Score Breakdown (Compact) */}
                                        {latestResult.scores && (
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                {Object.entries(latestResult.scores).slice(0, 4).map(([key, value]) => (
                                                    <div key={key} className="space-y-1">
                                                        <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-gray-500">
                                                            <span>{key.replace('_', ' ')}</span>
                                                            <span className="text-white">{Math.round(value)}</span>
                                                        </div>
                                                        <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${value}%` }}
                                                                className="h-full bg-white rounded-full opacity-60"
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {latestResult.summary && (
                                        <div className="mt-8 pt-6 border-t border-white/5">
                                            <div className="flex gap-3">
                                                <div className="w-1.5 h-auto bg-brand-500/30 rounded-full" />
                                                <p className="text-sm text-gray-400 italic leading-relaxed line-clamp-2">
                                                    "{latestResult.summary}"
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </GlassCard>
                        </div>
                    )}
                </div>

                {/* Right Column: Journey & Goals (1/3) */}
                <div className="space-y-6">

                    {/* Credit Balance Widget */}
                    <GlassCard className="!py-3 !px-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <RiCoinLine className="text-yellow-400" size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white/40 text-xs">Available Credits</p>
                            <p className={`text-xl font-bold ${credits !== null && credits > 0 ? 'text-white' : 'text-white/30'}`}>
                                {credits !== null ? credits : '—'}
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/dashboard/credits')}
                            className="text-white/30 hover:text-white/60 text-xs transition-colors flex-shrink-0"
                        >
                            View log →
                        </button>
                    </GlassCard>

                    {/* Call to Action Card - High Emphasis */}
                    <GlassCard className="relative overflow-hidden border-white/20 group">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />

                        <h2 className="text-xl font-bold text-white mb-2 relative z-10">Start your journey</h2>
                        <p className="text-sm text-gray-400 mb-6 relative z-10">
                            Practice with our AI interviewer to boost your confidence and skills.
                        </p>

                        <GlassButton
                            onClick={() => navigate('/interview-mode')}
                            className="w-full relative z-10 shadow-white/10"
                        >
                            Start New Interview
                        </GlassButton>
                    </GlassCard>

                    {/* Get Started / Quick Actions */}
                    {!getStartedComplete ? (
                        <GlassCard>
                            <h3 className="text-lg font-semibold text-white mb-1">Get Started</h3>
                            <p className="text-xs text-zinc-500 mb-5">Complete these milestones to unlock more.</p>
                            <div className="space-y-4">
                                {[
                                    { done: m1Done, current: Math.min(jobsDone, JOBS_GOAL), total: JOBS_GOAL, label: 'Job practice sessions' },
                                    { done: m2Done, current: Math.min(messageCount, MESSAGES_GOAL), total: MESSAGES_GOAL, label: 'Messages sent' },
                                    { done: m3Done, current: Math.min(resumeCount, RESUMES_GOAL), total: RESUMES_GOAL, label: 'Resumes created' },
                                ].map(({ done, current, total, label }) => (
                                    <div key={label} className="flex items-center gap-3">
                                        {done
                                            ? <RiCheckboxCircleFill className="text-green-400 text-xl flex-shrink-0" />
                                            : <RiCheckboxBlankCircleLine className="text-zinc-600 text-xl flex-shrink-0" />
                                        }
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className={`text-xs font-medium ${done ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>{label}</span>
                                                <span className="text-xs text-zinc-600 ml-2 flex-shrink-0">{current}/{total}</span>
                                            </div>
                                            <div className="h-1 w-full bg-white/[0.06] rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${(current / total) * 100}%` }}
                                                    transition={{ duration: 0.6, ease: 'easeOut' }}
                                                    className={`h-full rounded-full ${done ? 'bg-green-500/60' : 'bg-white/40'}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </GlassCard>
                    ) : (
                        <div className="space-y-3">
                            <GlassCard
                                className="flex items-center gap-4 cursor-pointer hover:bg-white/[0.06] transition-colors"
                                onClick={() => navigate('/dashboard/messages')}
                            >
                                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                                    <RiMessage2Line className="text-blue-400 text-lg" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-white">Send a message</p>
                                    <p className="text-xs text-zinc-500 mt-0.5">Reach out to recruiters or connections</p>
                                </div>
                                <RiArrowRightUpLine className="text-zinc-600 flex-shrink-0" />
                            </GlassCard>
                            <GlassCard
                                className="flex items-center gap-4 cursor-pointer hover:bg-white/[0.06] transition-colors"
                                onClick={() => navigate('/dashboard/resumes')}
                            >
                                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
                                    <RiFileTextLine className="text-purple-400 text-lg" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-white">Generate a resume</p>
                                    <p className="text-xs text-zinc-500 mt-0.5">Create a tailored resume with AI</p>
                                </div>
                                <RiArrowRightUpLine className="text-zinc-600 flex-shrink-0" />
                            </GlassCard>
                        </div>
                    )}

                </div>
            </div>


            {/* Analytics Preview */}
            <AnalyticsPreview data={analyticsData} onViewAnalytics={() => navigate('/dashboard/analytics')} />

            {/* Reuse Confirmation Dialog */}
            <ConfirmDialog
                isOpen={reuseConfirmOpen}
                title="Retake Practice Interview?"
                message="This will create a new interview session with the same settings (Role, Description, etc.) as the original. This action will deduct 5 credits from your balance."
                confirmText="Confirm & Start (5 Credits)"
                cancelText="Cancel"
                variant="info"
                onCancel={() => {
                    setReuseConfirmOpen(false);
                    setSelectedForReuse(null);
                }}
                onConfirm={handleReuseConfirm}
            />
        </div >
    );
}
